import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { ContextService } from '@maximizer/core/shared/data-access';
import { InsightsService } from '@maximizer/core/shared/insights';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import { OAuth2Service } from '@maximizer/oauth2/data-access';
import {
  MailboxType,
  OutlookInsightsEvents,
  OutlookService,
  OutlookStore,
  getDecodedAccessToken,
} from '@maximizer/outlook/shared/data-access';
import { normalizeError } from '@maximizer/shared/util';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { Subject } from 'rxjs';

@Component({
  selector: 'maximizer-root-home',
  template: '',
  styles: `
    :host {
      display: block;
      height: 100vh;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [KENDO_BUTTON, UI_LOADER],
})
export class HomeComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  private readonly router = inject(Router);
  private readonly contextService = inject(ContextService);
  private readonly oauth2Service = inject(OAuth2Service);
  private readonly outlookService = inject(OutlookService);
  private readonly outlookStore = inject(OutlookStore);
  private readonly insightsService = inject(InsightsService);

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async initializeComponent(): Promise<void> {
    try {
      const authError = sessionStorage.getItem('authError');
      if (authError) {
        sessionStorage.removeItem('authError');
        this.clearAuthAndRedirectToLogin(true, authError);
        return;
      }

      if (!(await this.isAuthenticated())) {
        this.clearAuthAndRedirectToLogin();
        return;
      }

      await this.handleMailboxInitialization();
    } catch (error) {
      this.handleInitializationError(error);
    }
  }

  private async isAuthenticated(): Promise<boolean> {
    const hasToken = !!this.contextService?.token;
    const isAuthorized = this.outlookStore.isAuthorized();

    if (!hasToken && isAuthorized) {
      const restoredToken = await this.oauth2Service.syncTokenFromStorage({
        skipRefresh: true,
      });

      return !!(restoredToken?.access_token ?? this.contextService?.token);
    }

    return hasToken && isAuthorized;
  }

  private async handleMailboxInitialization(): Promise<void> {
    try {
      await this.loadIsSharedMailbox();
      this.navigateBasedOnEmailContext();
    } catch (error) {
      this.handleMailboxError(error);
    }
  }

  private async loadIsSharedMailbox(): Promise<void> {
    const isSharedMailbox = await this.outlookService.isSharedMailbox();
    this.outlookStore.setIsSharedMailbox(isSharedMailbox);
    this.outlookStore.setMailboxType(
      isSharedMailbox ? MailboxType.Shared : MailboxType.Personal,
    );
  }

  private navigateBasedOnEmailContext(): void {
    const item = Office?.context?.mailbox?.item;

    if (item?.itemType === 'appointment') {
      this.router.navigate([
        this.outlookService.isCalendarCompose
          ? '/calendar-compose'
          : '/calendar-read',
      ]);
      return;
    }

    if (!item?.itemId) {
      this.router.navigate(['/compose-email']);
      return;
    }

    this.router.navigate(['/read-email']);
  }

  private handleMailboxError(error: unknown): void {
    this.logError('home-mailbox-error', error);
    this.handleMailboxRetryError();
  }

  private handleInitializationError(error: unknown): void {
    this.logError('home-initialization-error', error);
    this.clearAuthAndRedirectToLogin();
  }

  private async clearAuthAndRedirectToLogin(
    showNotification = false,
    errorType?: string,
  ): Promise<void> {
    let queryParam = '';
    if (showNotification) {
      queryParam = errorType ? `?error=${errorType}` : '?error=auth';
    }
    const loginUrl = `${window.location.origin}/login${queryParam}`;
    await this.oauth2Service.clearAuth('home-component-login-redirect', {
      trigger: 'clearAuthAndRedirectToLogin',
      showNotification,
      errorType: errorType ?? '',
      loginUrl,
    });
    window.location.href = loginUrl;
  }

  private async handleMailboxRetryError(): Promise<void> {
    if (sessionStorage.getItem('mailboxRetry') === 'true') {
      await this.clearAuthAndRedirectToLogin();
      sessionStorage.removeItem('mailboxRetry');
      return;
    }
    sessionStorage.setItem('mailboxRetry', 'true');
    window.location.reload();
  }

  private logError(eventName: string, error: unknown): void {
    const errorMessage = normalizeError(error);
    const errorObj = error instanceof Error ? error : new Error(errorMessage);

    this.logHomeEvent(eventName, errorMessage);

    this.insightsService.trackException({
      error: errorObj,
      properties: { component: 'HomeComponent' },
    });
  }

  private logHomeEvent(name: string, error?: string): void {
    this.insightsService.trackEvent({ name }, SeverityLevel.Information, {
      eventId: OutlookInsightsEvents.OutlookAuth,
      decodedToken: getDecodedAccessToken(this.contextService.token),
      ...(error && { error }),
    });
  }
}
